import React, { useEffect, useMemo } from 'react';
// @ts-ignore
import styles from './MonitoringCenterList.module.scss';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import { useAppContext } from '../../../../Services/AppContext';
import MonitoringCenterListItem, {
	IMonitoringCenterListItem,
} from './MonitoringCenterListItem';
import MonitoringCenterListHeader from './MonitoringCenterListHeader';
import Loading from '../../../../Components/Loading';
import InfiniteScroll from 'react-infinite-scroll-component';
import { RequestPriority } from '../../Enums/RequestPriority.enum';
import { RequestState } from '../../Enums/RequestState.enum';
import {
	useTermItemsQuery,
	useWorkplaceMemberQuery,
} from '../../../../graphql/graphql';
import { PriorityPickerValue } from '../../../Base/Components/PriorityPicker/PriorityPicker';
import { Theme } from '../../../../Enums/Theme.enum';
import useSocket from '../../../Base/Hooks/useSocket';
import { Socket } from '../../../Base/Enums/Socket.enum';
import OverlayLoading from '../../../../Components/OverlayLoading';

interface Props {
	flatData: any[];
	onLoadMore: () => void;
	onRefresh: () => void;
	isLoading: boolean;
	hasMoreData: boolean;
	renderItem?: (item: any) => React.ReactElement;
	customHeader?: React.ReactElement;
	isStatic?: boolean;
	isFlatDataLoading: boolean;
}

interface MonitoringCenterListTableProps {
	flatData: any;
	onRefresh: () => void;
	renderItem?: (item: any) => React.ReactElement;
	customHeader?: React.ReactElement;
}

const WRAPPER_ID = 'monitoringCenterListWrapper';

const MonitoringCenterListTable: React.FC<MonitoringCenterListTableProps> = ({
	flatData,
	onRefresh,
	renderItem,
	customHeader,
}) => {
	const { userData, workplaceId } = useAppContext();

	const priorityTermItems = useTermItemsQuery({
		termType: 'case_priority',
		take: 999,
	});

	const workplaceMember = useWorkplaceMemberQuery({
		userId: userData?.id,
		workplaceId,
	});

	const workplaceMemberId = useMemo(() => {
		return workplaceMember?.data?.workplaceMember?.id;
	}, [workplaceMember]);

	const priorityOptions: PriorityPickerValue[] = useMemo(() => {
		return [
			{
				id:
					priorityTermItems?.data?.termItems?.data?.find(
						item => item?.value === RequestPriority.HIGH,
					)?.id ?? '',
				key: RequestPriority.HIGH,
				title: 'component.monitoringCenter.priority.high',
				color: Theme.RED,
			},
			{
				id:
					priorityTermItems?.data?.termItems?.data?.find(
						item => item?.value === RequestPriority.MEDIUM,
					)?.id ?? '',
				key: RequestPriority.MEDIUM,
				title: 'component.monitoringCenter.priority.medium',
				color: Theme.ORANGE,
			},
			{
				id:
					priorityTermItems?.data?.termItems?.data?.find(
						item => item?.value === RequestPriority.LOW,
					)?.id ?? '',
				key: RequestPriority.LOW,
				title: 'component.monitoringCenter.priority.low',
				color: Theme.GREEN,
			},
			{
				id:
					priorityTermItems?.data?.termItems?.data?.find(
						item => item?.value === RequestPriority.NONE,
					)?.id ?? '',
				key: RequestPriority.NONE,
				title: 'component.monitoringCenter.priority.none',
				color: Theme.GREY,
			},
		];
	}, [priorityTermItems]);

	if (priorityTermItems.isLoading || workplaceMember.isLoading) {
		return <Loading />;
	}

	return (
		<table style={{ width: '100%' }}>
			{customHeader ?? <MonitoringCenterListHeader />}
			<tbody>
				{flatData?.map((item: any) => {
					const getAttendingDoctor = () => {
						const firstRelation = item.patient?.userRelations[0];
						let fullName = undefined;
						if (firstRelation) {
							const salutationBefore =
								firstRelation?.relationUser?.profile?.salutationBefore;
							const lastName = firstRelation?.relationUser?.profile?.lastName;
							if (lastName) {
								fullName = lastName;
							}
							if (salutationBefore) {
								fullName = salutationBefore + ' ' + lastName;
							}
						}
						return {
							id: firstRelation?.id,
							fullName: fullName,
						};
					};

					const itemData: IMonitoringCenterListItem = {
						id: item.id,
						number: item.code,
						priority: item?.priority?.value,
						state: item.isNew ? RequestState.NEW : RequestState.IN_PROGRESS,
						caseType:
							item?.caseType?.rootCaseType?.label ?? item.caseType?.label,
						patient: {
							id: item?.patient?.id,
							fullName: item?.patient?.profile?.fullName,
							birthNumber: item?.patient?.profile?.formattedBirthNumber,
							attendingDoctor: getAttendingDoctor(),
							originalId: item?.patient?.originalId,
						},
						assignedDoctor: {
							id: item?.doctor?.id,
							shortedName:
								item?.doctor?.profile?.salutationBefore +
								' ' +
								item?.doctor?.profile?.lastName,
							fullName: item?.doctor?.profile?.fullName,
						},
						workplace: {
							id: item?.workplace?.id,
							title: item?.workplace?.doctorDisplayName,
							shortcut: item?.workplace?.codeName,
						},
						diagnosis: {
							id: '',
							title: item?.diagnosis,
						},
						note: item?.lastNote,
						createdAt: item.createdAt,
						lastChangeAt: item.updatedAt,
						isNew: item.isNew,
						isMyCase: item.isMyCase,
						isAssigned: item.isAssigned,
						totalUnreadCaseEntries: item.totalUnreadCaseEntries,
						medicalReports: item.medicalReports,
						caseTypeCode: item.caseType.code,
						medicationLasted: item.medicationLasted,
					};

					return (
						<React.Fragment key={itemData.id}>
							{renderItem ? (
								renderItem(itemData)
							) : (
								<MonitoringCenterListItem
									item={itemData}
									priorityOptions={priorityOptions}
									onRefresh={onRefresh}
									workplaceMemberId={workplaceMemberId}
								/>
							)}
						</React.Fragment>
					);
				})}
			</tbody>
		</table>
	);
};

const MonitoringCenterList: React.FC<Props> = ({
	flatData,
	onLoadMore,
	onRefresh,
	hasMoreData,
	renderItem,
	customHeader,
	isStatic = false,
	isFlatDataLoading,
}) => {
	useSocket(Socket.CASE_CREATED, onRefresh);

	if (isStatic) {
		return (
			<>
				{isFlatDataLoading && <OverlayLoading />}
				<MonitoringCenterListTable
					flatData={flatData}
					onRefresh={onRefresh}
					renderItem={renderItem}
					customHeader={customHeader}
				/>
			</>
		);
	}

	return (
		<div className={styles.wrapper}>
			{isFlatDataLoading && <OverlayLoading />}
			<ScrollContainer wrapperId={WRAPPER_ID}>
				<InfiniteScroll
					dataLength={flatData.length}
					refreshFunction={onRefresh}
					next={onLoadMore}
					hasMore={hasMoreData}
					loader={<Loading />}
					scrollableTarget={WRAPPER_ID}>
					<MonitoringCenterListTable
						flatData={flatData}
						onRefresh={onRefresh}
						renderItem={renderItem}
						customHeader={customHeader}
					/>
				</InfiniteScroll>
			</ScrollContainer>
		</div>
	);
};

export default MonitoringCenterList;
